import React from 'react';
import './TimelineComponent.css';

import { PropTypesDay, IEvent } from '../interfaces';
import { EventCategoryColor } from '../enums';
import { getRelativeEventTime, dateToMinutesInDay, formattedEventTime } from '../utils';
import Color from '../colors';

import ModalDialog from './ModalDialog';
import { DUMMY_EVENT } from '../constants';

const minutes = 60;
const timeLabelOffset = 6;  // Adjusted for better spacing
const timeMarkerOffset = 0;  // Subtle time marker
const labelSpaceVertical = 8; // Increased spacing for clean look
const labelSpaceHorizontal = 100; // Increased for better readability
const trackSpace = 50; // More space between events for clarity
const trackStartHeight = 50; // Adjusted track start height for uniformity
const timeLabels = Array.from(Array(24).keys()).map(i => `${i % 12 === 0 ? 12 : i % 12}${i % 24 < 12 ? 'AM' : 'PM'}`);

function doEventsOverlap(event1: IEvent, event2: IEvent): boolean {
	const event1Start = event1.start.getTime();
	const event1End = event1Start + event1.duration * 60 * 1000;
	const event2Start = event2.start.getTime();
	const event2End = event2Start + event2.duration * 60 * 1000;
	
	// Events overlap if one starts before the other ends
	return event1Start < event2End && event2Start < event1End;
}

function assignEventsToTracks(events: IEvent[]): IEvent[][] {
	// Sort events by start time
	const sortedEvents = [...events].sort((a, b) => a.start.getTime() - b.start.getTime());
	
	const tracks: IEvent[][] = [];
	
	for (const event of sortedEvents) {
		// Find the first track where this event doesn't overlap with any existing event
		let assigned = false;
		for (let trackIndex = 0; trackIndex < tracks.length; trackIndex++) {
			const track = tracks[trackIndex];
			const hasOverlap = track.some(existingEvent => doEventsOverlap(event, existingEvent));
			
			if (!hasOverlap) {
				track.push(event);
				assigned = true;
				break;
			}
		}
		
		// If no existing track can accommodate this event, create a new track
		if (!assigned) {
			tracks.push([event]);
		}
	}
	
	return tracks;
}

class TimelineComponent extends React.Component<PropTypesDay> {
	scrollContainerRef: React.RefObject<HTMLDivElement>;
	state: {
		modalShow: boolean;
		modalFormattedTime: string;
		selectedEvent: IEvent;
	};

	constructor(props: PropTypesDay) {
		super(props);
		this.scrollContainerRef = React.createRef();

		this.state = {
			modalShow: false,
			modalFormattedTime: '',
			selectedEvent: DUMMY_EVENT
		};
	}

	componentDidMount() {
		if (this.scrollContainerRef.current) {
			// Find the earliest event start time
			const earliestEvent = this.props.day.events.reduce((earliest, event) => {
				return event.start < earliest.start ? event : earliest;
			}, this.props.day.events[0]);
			
			const earliestHour = earliestEvent ? earliestEvent.start.getHours() : 0;
			const earliestMinute = earliestEvent ? earliestEvent.start.getMinutes() : 0;
			const earliestMinutes = earliestHour * 60 + earliestMinute;
			const startHour = Math.floor(earliestMinutes / 60);
			const startOffsetMinutes = startHour * 60;
			
			if (this.props.showAsToday) {
				this.scrollContainerRef.current.scrollLeft = this.computeSliderPos(startOffsetMinutes) - labelSpaceHorizontal;
			} else {
				// Scroll to show the first event
				this.scrollContainerRef.current.scrollLeft = 0;
			}
		}
	}

	computeSliderPos(startOffsetMinutes: number = 0) {
		const now = new Date();
		const nowMinutes = dateToMinutesInDay(now);
		return (labelSpaceHorizontal / minutes) * (nowMinutes - startOffsetMinutes);
	}

	handleEventListItemClick(event: IEvent) {
		this.setState({
			modalShow: true,
			modalFormattedTime: formattedEventTime(event),
			selectedEvent: event
		});
	}

	render() {
		const tracks = assignEventsToTracks(this.props.day.events);
		
		// Find the earliest event start time
		const earliestEvent = this.props.day.events.reduce((earliest, event) => {
			return event.start < earliest.start ? event : earliest;
		}, this.props.day.events[0]);
		
		const earliestHour = earliestEvent ? earliestEvent.start.getHours() : 0;
		const earliestMinute = earliestEvent ? earliestEvent.start.getMinutes() : 0;
		const earliestMinutes = earliestHour * 60 + earliestMinute;
		
		// Round down to the nearest hour for cleaner display
		const startHour = Math.floor(earliestMinutes / 60);
		const startOffsetMinutes = startHour * 60;
		
		// Calculate how many hours we need to display (from start hour to end of day)
		// For Friday (index 0), extend to show 12AM at the end
		const isFriday = this.props.day.index === 0;
		const hoursToDisplay = isFriday ? (24 - startHour + 1) : (24 - startHour);
		let displayTimeLabels;
		if (isFriday && startHour < 24) {
			// Show from startHour to 23, then add 12AM (hour 0)
			const regularHours = timeLabels.slice(startHour, 24);
			displayTimeLabels = [...regularHours, timeLabels[0]]; // Add 12AM at the end
		} else {
			displayTimeLabels = timeLabels.slice(startHour, startHour + hoursToDisplay);
		}

		return (
			<div id="timeline" ref={this.scrollContainerRef}>
				<ModalDialog
					show={this.state.modalShow}
					onHide={() => this.setState({ modalShow: false })}
					formattedTime={this.state.modalFormattedTime}
					event={this.state.selectedEvent}
				/>
				<div id="timeline-label-container">
					{displayTimeLabels.map((label, index) => {
						// For Friday, the last label (12AM) should be at hour 24 (next day)
						const actualHourIndex = isFriday && index === displayTimeLabels.length - 1 
							? 24 
							: startHour + index;
						return (
							<div key={`timeline-label-${actualHourIndex}`}>
								<p
									className="timeline-label"
									style={{
										top: labelSpaceVertical,
										left: index * labelSpaceHorizontal + timeLabelOffset,
										width: labelSpaceHorizontal
									}}
								>
									{label}
								</p>
								<div
									className="timeline-label-marker"
									style={{
										left: index * labelSpaceHorizontal - timeMarkerOffset,
										background: '#D3D3D3'
									}}
								/>
							</div>
						);
					})}
				</div>
				{this.props.showAsToday && (
					<div
						id="timeline-slider"
						style={{
							left: this.computeSliderPos(startOffsetMinutes)
						}}
					>
						<svg className="triangle-pointer top" fill="#ff7f7f" viewBox="0 0 100 100">
							<path d="M0 0 L50 100 L100 0 Z"></path>
						</svg>
						<svg className="triangle-pointer bottom" fill="#ff7f7f" viewBox="0 0 100 100">
							<path d="M0 100 L50 2 L100 100 Z"></path>
						</svg>
					</div>
				)}
				<div id="timeline-tracks-container">
					{tracks.map((track, trackIndex) => (
						<div key={`timeline-track-${trackIndex}-container`}>
							{track.map((event, eventIndex) => {
								const eventMinutes = dateToMinutesInDay(event.start);
								const adjustedLeft = (labelSpaceHorizontal / minutes) * (eventMinutes - startOffsetMinutes);
								return (
									<div
										key={`timeline-track-${trackIndex}-${eventIndex}`}
										className={`timeline-track-item gantt-style ${this.props.showAsToday ? getRelativeEventTime(event) : this.props.relativeDayTime}`}
										style={{
											width: (labelSpaceHorizontal / minutes) * event.duration,
											left: adjustedLeft,
											top: trackStartHeight + trackSpace * trackIndex
										}}
										onClick={() => this.handleEventListItemClick(event)}
									>
										<p>{event.name}</p>
										<div
											className="timeline-track-bar"
											style={{
												background: EventCategoryColor[event.category] || Color.Overflow,
												height: '100%',
												borderRadius: '6px', // Rounded corners for a smooth look
											}}
										/>
									</div>
								);
							})}
						</div>
					))}
				</div>
			</div>
		);
	}
}

export default TimelineComponent;
